// PasskeySection — passkey enrolment + management for the signed-in user.
//
// Ported from b2b-arpay-frontend's AddPasskeySection, adapted to the
// payouts-panel stack: the axios apiClient (bearer + silent-refresh handled by
// the interceptor, so no authedFetch is needed here) and the Strata CSS
// classes. Lets a user register a new passkey (with an optional device label,
// stored via the X-Challenge-Name header), then list, rename (inline), or
// delete their existing passkeys.
import { useCallback, useEffect, useState } from "react";
import {
  startRegistration,
  browserSupportsWebAuthn,
  WebAuthnError,
} from "@simplewebauthn/browser";

import { I } from "@/components/Icons";
import {
  webauthnRegisterBegin,
  webauthnRegisterFinish,
  listWebauthnCredentials,
  renameWebauthnCredential,
  deleteWebauthnCredential,
  type Passkey,
} from "@/api/auth";
import { useAuth } from "@/store/auth";

type Status =
  | { kind: "idle" }
  | { kind: "working" }
  | { kind: "success" }
  | { kind: "error"; message: string };

/** Per-row manage state: which passkey is being edited and any inline error. */
type RowState =
  | { kind: "view" }
  | { kind: "editing"; value: string }
  | { kind: "busy" };

/** Formats an ISO date string as a short locale date; "never"/"—" fallbacks. */
function formatDate(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "—";
  return new Date(ms).toLocaleDateString(undefined, { dateStyle: "medium" });
}

// friendlyPasskeyError maps a register ceremony / transport failure to a
// user-facing string. A cancelled/dismissed OS prompt is not a real error.
function friendlyPasskeyError(err: unknown): string {
  if (
    (err instanceof WebAuthnError && err.code === "ERROR_CEREMONY_ABORTED") ||
    (err instanceof DOMException &&
      (err.name === "NotAllowedError" || err.name === "AbortError"))
  ) {
    return "Passkey setup was cancelled.";
  }
  return "Adding a passkey failed. Please try again.";
}

export function PasskeySection() {
  const accessToken = useAuth((s) => s.accessToken);

  const [supported, setSupported] = useState(false);
  const [name, setName] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);

  // Per-row UI state keyed by passkey id, plus a per-row inline error message.
  const [rowState, setRowState] = useState<Record<number, RowState>>({});
  const [rowError, setRowError] = useState<Record<number, string>>({});

  useEffect(() => {
    setSupported(browserSupportsWebAuthn());
  }, []);

  // Reload the user's passkeys. Fail open: on any error just show no list
  // (the add-form still works).
  const reloadPasskeys = useCallback(async (): Promise<void> => {
    try {
      setPasskeys(await listWebauthnCredentials());
    } catch {
      setPasskeys([]);
    }
  }, []);

  // Load existing passkeys once we have a session and WebAuthn is available.
  useEffect(() => {
    if (!browserSupportsWebAuthn() || !accessToken) return;
    let cancelled = false;
    listWebauthnCredentials()
      .then((list) => {
        if (!cancelled) setPasskeys(list);
      })
      .catch(() => {
        if (!cancelled) setPasskeys([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  if (!supported || !accessToken) return null;

  async function handleAdd() {
    setStatus({ kind: "working" });
    try {
      const { options, challengeId } = await webauthnRegisterBegin(name);
      const attestation = await startRegistration({ optionsJSON: options });
      await webauthnRegisterFinish(challengeId, name, attestation);
      setStatus({ kind: "success" });
      setName("");
      await reloadPasskeys();
    } catch (err) {
      setStatus({ kind: "error", message: friendlyPasskeyError(err) });
    }
  }

  function setRow(id: number, next: RowState) {
    setRowState((prev) => ({ ...prev, [id]: next }));
  }

  function clearRowError(id: number) {
    setRowError((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function setRowErrorMessage(id: number, message: string) {
    setRowError((prev) => ({ ...prev, [id]: message }));
  }

  function startEdit(pk: Passkey) {
    clearRowError(pk.id);
    setRow(pk.id, { kind: "editing", value: pk.name ?? "" });
  }

  function cancelEdit(id: number) {
    clearRowError(id);
    setRow(id, { kind: "view" });
  }

  async function saveRename(id: number, value: string) {
    const trimmed = value.trim();
    if (!trimmed) {
      setRowErrorMessage(id, "Enter a name for this passkey.");
      return;
    }
    clearRowError(id);
    setRow(id, { kind: "busy" });
    try {
      await renameWebauthnCredential(id, trimmed);
      setRow(id, { kind: "view" });
      await reloadPasskeys();
    } catch {
      setRowErrorMessage(id, "Could not rename the passkey.");
      setRow(id, { kind: "editing", value });
    }
  }

  async function handleDelete(pk: Passkey) {
    const label = pk.name || "this passkey";
    if (
      typeof window !== "undefined" &&
      !window.confirm(`Remove ${label}? You won't be able to sign in with it.`)
    ) {
      return;
    }
    clearRowError(pk.id);
    setRow(pk.id, { kind: "busy" });
    try {
      await deleteWebauthnCredential(pk.id);
      await reloadPasskeys();
    } catch {
      setRowErrorMessage(pk.id, "Could not remove the passkey.");
      setRow(pk.id, { kind: "view" });
    }
  }

  const working = status.kind === "working";

  return (
    <section className="card" style={{ padding: 16, maxWidth: 640 }}>
      <div className="stack-2" style={{ marginBottom: 12 }}>
        <div className="eyebrow">Security</div>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Passkeys</h3>
        <p className="dim" style={{ fontSize: 12, marginTop: 2 }}>
          Add a passkey to sign in instantly with your fingerprint, face, or
          device PIN — no email code needed.
        </p>
      </div>

      {passkeys.length > 0 && (
        <div style={{ borderTop: "1px solid var(--hair)" }}>
          {passkeys.map((pk) => {
            const rs: RowState = rowState[pk.id] ?? { kind: "view" };
            const err = rowError[pk.id];
            const busy = rs.kind === "busy";
            return (
              <div
                key={pk.id}
                style={{
                  padding: "10px 0",
                  borderBottom: "1px solid var(--hair)",
                }}
              >
                {rs.kind === "editing" ? (
                  <div
                    className="row gap-8"
                    style={{ alignItems: "center", flexWrap: "wrap" }}
                  >
                    <input
                      className="input"
                      type="text"
                      value={rs.value}
                      onChange={(e) =>
                        setRow(pk.id, { kind: "editing", value: e.target.value })
                      }
                      maxLength={64}
                      autoFocus
                      aria-label="Passkey name"
                      style={{ flex: 1, minWidth: 160 }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void saveRename(pk.id, rs.value);
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit(pk.id);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn accent sm"
                      onClick={() => void saveRename(pk.id, rs.value)}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn ghost sm"
                      onClick={() => cancelEdit(pk.id)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div
                    className="row"
                    style={{
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>
                      {pk.name || "Passkey"}
                    </span>
                    <span className="row gap-8" style={{ alignItems: "center" }}>
                      <span
                        className="dim"
                        style={{ fontSize: 11, textAlign: "right" }}
                      >
                        Added {formatDate(pk.created_at)} · Last used{" "}
                        {formatDate(pk.last_used_at)}
                      </span>
                      <button
                        type="button"
                        className="btn ghost sm"
                        onClick={() => startEdit(pk)}
                        disabled={busy}
                        aria-label={`Rename ${pk.name || "passkey"}`}
                      >
                        <I.Edit size={11} /> Rename
                      </button>
                      <button
                        type="button"
                        className="btn ghost sm"
                        onClick={() => void handleDelete(pk)}
                        disabled={busy}
                        aria-label={`Remove ${pk.name || "passkey"}`}
                        style={{ color: "var(--neg)" }}
                      >
                        {busy ? "…" : <><I.Trash size={11} /> Remove</>}
                      </button>
                    </span>
                  </div>
                )}

                {err && (
                  <div
                    style={{ color: "var(--neg)", fontSize: 11, marginTop: 6 }}
                  >
                    {err}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {status.kind === "success" && (
        <div style={{ color: "var(--pos)", fontSize: 12, marginTop: 12 }}>
          Passkey added. You can now use it to sign in.
        </div>
      )}
      {status.kind === "error" && (
        <div style={{ color: "var(--neg)", fontSize: 12, marginTop: 12 }}>
          {status.message}
        </div>
      )}

      <div
        className="row gap-8"
        style={{ alignItems: "flex-end", flexWrap: "wrap", marginTop: 14 }}
      >
        <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
          <label className="label" htmlFor="passkey-name">
            Device name (optional)
          </label>
          <input
            id="passkey-name"
            className="input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={working}
            placeholder="e.g. MacBook Touch ID"
            maxLength={64}
          />
        </div>
        <button
          type="button"
          className="btn accent"
          onClick={() => void handleAdd()}
          disabled={working}
          style={{ height: 38 }}
        >
          {working ? "Waiting for passkey…" : (
            <>
              <I.Plus size={12} /> Add a passkey
            </>
          )}
        </button>
      </div>
    </section>
  );
}
